import { z } from 'zod';
import { ENTITY_CHANGE_KEYS } from '@xiltrion/entidad';
import { appCodeSchema, entityProfileSchema, entitySlugSchema } from './claims.js';
import { roleGrantSchema } from './roles.js';
/** Ruta por defecto, bajo el API de la app, del endpoint de aprovisionamiento (spec §5). */
export const DEFAULT_PROVISIONING_PATH = '/api/v1/suite/tenants';
/** `POST <provisioning_path>`: la suite pide a la app crear (o confirmar) el tenant. Idempotente por slug. */
export const provisionTenantRequestSchema = z.object({
    entity: z.object({
        slug: entitySlugSchema,
        name: z.string().min(1).max(200),
        profile: entityProfileSchema,
        nit: z.string().max(20).nullable().optional(),
        /**
         * Dónde deben vivir los datos de la entidad (`default`, `bogota`, `cliente-x`…). Cada app traduce el código a uno
         * de sus servidores de bases (`TENANT_DB_SERVERS`, `@xiltrion/tenancy`); las credenciales nunca viajan desde la
         * suite. Una ubicación que la app no conoce es un error 422 `UNKNOWN_DATA_LOCATION`.
         */
        dataLocation: z.string().regex(/^[a-z][a-z0-9-]{1,30}$/).default('default'),
    }),
    admin: z.object({ email: z.string().email(), displayName: z.string().min(1).max(160) }),
    modules: z.array(z.string()).default([]),
    suite: z.object({ issuer: z.string().url(), clientId: z.string() }),
});
export const provisionTenantResponseSchema = z.object({
    ok: z.literal(true),
    /** Referencia interna de la app (id del tenant, nombre de la base…), solo informativa. */
    externalRef: z.string().optional(),
    /** Ya existía: la app no lo volvió a crear. */
    existed: z.boolean().optional(),
    warnings: z.array(z.string()).optional(),
});
/** `PUT <provisioning_path>/:slug/modules` */
export const setTenantModulesRequestSchema = z.object({ modules: z.array(z.string()) });
/** `PATCH <provisioning_path>/:slug` */
/**
 * `PATCH <provisioning_path>/:slug`: la entidad cambió en la suite. `status` y `name` se aplican al tenant; `changed`
 * dice qué datos de la entidad (§3.11, §3.12 y §3.15) debe volver a leer la app por la API de servicio (ficha, marca,
 * calendario, fuentes, correo, organigrama): basta con descartar su caché de eso (con `units`, reconciliar su copia del
 * organigrama). Una app que no lo conoce lo ignora y su caché vence sola.
 */
export const patchTenantRequestSchema = z.object({
    status: z.enum(['ACTIVE', 'SUSPENDED', 'ARCHIVED']).optional(),
    name: z.string().min(1).max(200).optional(),
    changed: z.array(z.enum(ENTITY_CHANGE_KEYS)).optional(),
});
/** `GET <provisioning_path>/:slug` */
export const tenantStatusResponseSchema = z.object({ slug: entitySlugSchema, exists: z.boolean(), status: z.string().optional(), modules: z.array(z.string()).optional(), externalRef: z.string().optional() });
/** Catálogo de módulos que la app declara al registrarse en la suite. */
export const appModuleDefSchema = z.object({
    code: z.string().regex(/^[a-z][a-z0-9_.-]*$/),
    name: z.string().min(1).max(120),
    description: z.string().max(400).default(''),
    /**
     * Se enciende al habilitar la app para una entidad o, si requiere otros módulos, al encender uno de ellos con todo lo
     * que necesita (los submódulos de un paquete llegan con su raíz).
     */
    defaultOn: z.boolean().default(true),
    /** No se puede apagar. */
    locked: z.boolean().default(false),
    /** Códigos de módulos de la misma app que necesita: encenderlo los enciende y apagar uno de ellos lo apaga. */
    dependsOn: z.array(z.string()).default([]),
});
/**
 * `PUT /api/v1/service/modules`: la app publica su catálogo de módulos (al arrancar y cuando cambie). La suite es el
 * único lugar donde se encienden y apagan (Entidad › Apps y módulos); la app no tiene pantalla propia para eso.
 */
export const appModuleCatalogSchema = z.object({ modules: z.array(appModuleDefSchema).min(1).max(200) });
/** Manifiesto de una app de la suite (lo que la consola registra). */
export const appManifestSchema = z.object({
    code: appCodeSchema,
    name: z.string().min(1).max(80),
    description: z.string().max(300).default(''),
    /** Ícono `mdi:*` del selector de apps. */
    icon: z.string().default('mdi:application-outline'),
    /** Índice 1–12 de la paleta categórica de `@xiltrion/ui`: el color de la app en el Inicio de la suite. */
    color: z.number().int().min(1).max(12).nullable().optional(),
    webUrl: z.string().url(),
    apiUrl: z.string().url(),
    provisioningPath: z.string().default(DEFAULT_PROVISIONING_PATH),
    redirectUris: z.array(z.string().url()).min(1),
    postLogoutRedirectUris: z.array(z.string().url()).default([]),
    modules: z.array(appModuleDefSchema).default([]),
});
/**
 * `PUT <provisioning_path>/:slug/users/:uid`: la suite avisa a la app que una persona cambió (nombre, correo, estado,
 * rol en la entidad o acceso a la app). La app busca su ficha por `sub` (su `external_subject`), luego por
 * `previousEmail` y luego por `email`; actualiza correo y nombre y aplica `status`. Si no existe y viene `ACTIVE`, la
 * crea con el rol básico (o administrador si `role` es `ADMIN`), igual que el alta JIT del ingreso. `INACTIVE`
 * significa que la persona ya no puede entrar a esta app en esta entidad (cuenta desactivada, membresía inactiva o sin
 * acceso a la app): la app la desactiva y cierra sus sesiones, sin borrar nada. Idempotente.
 */
export const syncTenantUserRequestSchema = z.object({
    uid: z.string().uuid(),
    sub: z.string(),
    email: z.string().email(),
    previousEmail: z.string().email().nullable().optional(),
    displayName: z.string().min(1).max(160),
    jobTitle: z.string().max(160).nullable().optional(),
    role: z.enum(['ADMIN', 'MEMBER']),
    status: z.enum(['ACTIVE', 'INACTIVE']),
    /**
     * Roles de la persona en esta app (§3.9). Si viene, la app reemplaza las asignaciones de roles de la ficha por estas
     * (valida contra su catálogo; 422 `ROLES_INVALID` si no se puede). Si no viene, la app no toca los roles.
     */
    roles: z.array(roleGrantSchema).optional(),
    /**
     * Dependencias de la persona (ids de la suite, §3.15). Si viene, la app reemplaza las de la ficha por estas,
     * conservando lo suyo de las que siguen (en SGDEA, si responde, aprueba o firma). Si no viene, no las toca.
     */
    units: z.array(z.string().uuid()).optional(),
});
export const syncTenantUserResponseSchema = z.object({ ok: z.literal(true), created: z.boolean().optional(), userId: z.string().optional() });
//# sourceMappingURL=provisioning.js.map